import React, { useEffect, useRef } from 'react';
import { Button, SearchInput, WindowedTable as Table } from '@economic/taco';
import useCachedQuery from '../shared/hooks/useCachedQuery';
import { CreateDepartmentDialog } from '../forms/Departments/CreateDepartment';
import { ViewProps } from './types';
import { t } from '../shared/components/l10n/l10n';
import View, { useViewFilter } from '../shared/components/View/View';
import { useTableSortPreferences } from '../shared/hooks/useTableSortPreference';

interface Props extends ViewProps<Department> {
    omitDistributionKeys?: boolean;
}

export interface Department {
    access: boolean;
    number: number;
    name: string;
    isDepartment: boolean;
}

const DepartmentsView = ({ onCreated: handleCreated, onSelected: handleSelected, omitDistributionKeys }: Props) => {
    const departmentsQuery = useCachedQuery<Department[]>(`/departments?includeDistributionKeys=${!omitDistributionKeys}`, []);
    const [filter, setFilter, filterBy] = useViewFilter();
    const tableSort = useTableSortPreferences('departments');

    // TODO: This should be removed once render issues with autoFocus being overwritten is resolved (EC-36855)
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTimeout(() => {
            ref.current?.focus();
        });
    }, []);
    // TODO: This should be removed once render issues with autoFocus being overwritten is resolved (EC-36855)

    return (
        <View>
            <View.Title>{t`LEFTMENU_SETTINGS_DEPARTMENTS`}</View.Title>

            <View.Header>
                <View.Header.Actions>
                    <Button
                        appearance="primary"
                        dialog={props => (
                            <CreateDepartmentDialog
                                {...props}
                                onCreate={department => {
                                    departmentsQuery.refetch();
                                    if (handleCreated) {
                                        handleCreated(department);
                                    }
                                }}
                            />
                        )}>
                        {t`FORM_DEPARTMENT_CREATETEXT`}
                    </Button>
                </View.Header.Actions>
                <View.Header.Filters>
                    <SearchInput
                        ref={ref}
                        autoFocus
                        onChange={event => setFilter(event.target.value)}
                        placeholder={t`FORM_LISTVIEW_FILTER_PLACEHOLDER`}
                        value={filter}
                    />
                </View.Header.Filters>
            </View.Header>

            <View.Content>
                <Table<Department>
                    {...tableSort}
                    data={departmentsQuery.data.filter(
                        department => department.access && (filterBy(department.name) || filterBy(department.number))
                    )}
                    onRowClick={row => handleSelected(row.values)}
                    dangerouslyHijackGlobalKeyboardNavigation>
                    <Table.Column accessor="number" headRenderer={t`FORM_FIELD_LABEL_NUMBER`} flex="0 0 75px" sort="asc" />
                    <Table.Column accessor="name" align="left" headRenderer={t`FORM_FIELD_LABEL_NAME`} />
                </Table>
            </View.Content>
        </View>
    );
};

export default DepartmentsView;
